"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Opens and closes its content by easing the grid row from 0fr to 1fr, so the
 * content below slides instead of jumping. Content mounts on open and leaves
 * after the closing transition; while closed it is inert. Reduced motion keeps
 * the change instant. Other attributes (`id`, `role`, `aria-label`) go on the
 * wrapper.
 */
export function Reveal({
  open,
  className,
  children,
  ...attributes
}: Omit<
  React.ComponentProps<"div">,
  "children" | "inert" | "onTransitionEnd"
> & { open: boolean; children: React.ReactNode }) {
  const [mounted, setMounted] = React.useState(open);

  if (open && !mounted) {
    setMounted(true);
  }

  return (
    <div
      {...attributes}
      inert={!open}
      onTransitionEnd={(event) => {
        if (
          !open &&
          event.target === event.currentTarget &&
          event.propertyName === "grid-template-rows"
        ) {
          setMounted(false);
        }
      }}
      className={cn(
        "grid transition-[grid-template-rows,opacity] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
        open
          ? "grid-rows-[1fr] opacity-100 duration-[420ms]"
          : "grid-rows-[0fr] opacity-0 duration-[260ms]",
        className,
      )}
    >
      <div className="min-h-0 overflow-hidden">{mounted ? children : null}</div>
    </div>
  );
}
